import React from 'react'
import { Edit3, Image as ImageIcon, Video as VideoIcon, RefreshCcw } from 'lucide-react'

const defaultTransform = {
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  rotation: 0,
  fitMode: 'cover'
}

const MaskElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  openMaskLibrary,
  startMaskContentPicker
}) => {
  if (!element || element.type !== 'mask') {
    return null
  }

  const maskAsset = element.maskAsset || {}
  const maskContent = element.maskContent
  const transform = maskContent?.transform || defaultTransform
  const maskSettings = element.maskSettings || { feather: 0, blur: 0, invert: false, opacity: 1 }

  const handleMaskSettingChange = (key, value) => {
    updateElementProperty(selectedElement, 'maskSettings', {
      ...maskSettings,
      [key]: value
    })
  }

  const handleTransformChange = (key, value) => {
    if (!maskContent) return
    updateElementProperty(selectedElement, 'maskContent', {
      ...maskContent,
      transform: {
        ...transform,
        [key]: value
      }
    })
  }

  const handleClearContent = () => {
    updateElementProperty(selectedElement, 'maskContent', null)
  }

  const resetTransform = () => {
    if (!maskContent) return
    updateElementProperty(selectedElement, 'maskContent', {
      ...maskContent,
      transform: { ...defaultTransform }
    })
  }

  return (
    <div className="space-y-6">
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-medium text-gray-700">Mask Frame</p>
            <p className="text-xs text-gray-500 truncate">{maskAsset.originalName || maskAsset.name || 'Untitled mask'}</p>
          </div>
          <button
            onClick={openMaskLibrary}
            className="px-3 py-1.5 text-xs bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100"
          >
            Change
          </button>
        </div>
        <div className="flex items-center space-x-3">
          <div className="w-16 h-16 rounded border overflow-hidden bg-gray-50 flex items-center justify-center">
            {maskAsset.thumbnailUrl || maskAsset.url ? (
              <img
                src={maskAsset.thumbnailUrl || maskAsset.url}
                alt={maskAsset.name || 'Mask frame'}
                className="w-full h-full object-contain"
              />
            ) : (
              <ImageIcon className="w-6 h-6 text-gray-400" />
            )}
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            <p>Type: {maskAsset.mediaType === 'video' ? 'Video' : 'Image'}</p>
            {maskAsset.width && maskAsset.height && (
              <p>Size: {maskAsset.width}x{maskAsset.height}px</p>
            )}
            {maskAsset.duration && (
              <p>Duration: {Math.round(maskAsset.duration)}s</p>
            )}
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">Masked Content</p>
          {maskContent && (
            <button
              onClick={handleClearContent}
              className="text-xs text-red-500 hover:text-red-600"
            >
              Remove
            </button>
          )}
        </div>

        {maskContent ? (
          <div className="flex items-center space-x-3">
            <div className="w-16 h-16 rounded border overflow-hidden bg-gray-50 flex items-center justify-center">
              {maskContent.type === 'video' ? (
                <VideoIcon className="w-6 h-6 text-gray-400" />
              ) : (
                <ImageIcon className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div className="text-xs text-gray-600 space-y-1">
              <p>{maskContent.type === 'video' ? 'Video content' : 'Image content'}</p>
              {maskContent.width && maskContent.height && (
                <p>{maskContent.width}x{maskContent.height}px</p>
              )}
              {maskContent.duration && (
                <p>Duration: {Math.round(maskContent.duration)}s</p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => startMaskContentPicker(element.id, 'image')}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-indigo-400"
            >
              <ImageIcon className="w-4 h-4" />
              <span className="text-sm">Use Photo</span>
            </button>
            <button
              onClick={() => startMaskContentPicker(element.id, 'video')}
              className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-indigo-400"
            >
              <VideoIcon className="w-4 h-4" />
              <span className="text-sm">Use Video</span>
            </button>
          </div>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-700">Content Adjustments</p>
          <button
            onClick={resetTransform}
            disabled={!maskContent}
            className="flex items-center text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
          >
            <RefreshCcw className="w-3 h-3 mr-1" />
            Reset
          </button>
        </div>

        <div className="space-y-3">
          <label className="flex items-center justify-between text-xs text-gray-500">
            <span>Zoom</span>
            <span>{(transform.scale || 1).toFixed(2)}x</span>
          </label>
          <input
            type="range"
            min="0.2"
            max="3"
            step="0.05"
            value={transform.scale || 1}
            disabled={!maskContent}
            onChange={(e) => handleTransformChange('scale', parseFloat(e.target.value))}
            className="w-full"
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Offset X</label>
              <input
                type="number"
                value={transform.offsetX || 0}
                disabled={!maskContent}
                onChange={(e) => handleTransformChange('offsetX', parseInt(e.target.value, 10) || 0)}
                className="w-full px-2 py-1 border border-gray-200 rounded"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Offset Y</label>
              <input
                type="number"
                value={transform.offsetY || 0}
                disabled={!maskContent}
                onChange={(e) => handleTransformChange('offsetY', parseInt(e.target.value, 10) || 0)}
                className="w-full px-2 py-1 border border-gray-200 rounded"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Rotation</label>
            <input
              type="number"
              value={transform.rotation || 0}
              disabled={!maskContent}
              onChange={(e) => handleTransformChange('rotation', parseInt(e.target.value, 10) || 0)}
              className="w-full px-2 py-1 border border-gray-200 rounded"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Fit Mode</label>
            <select
              value={transform.fitMode || 'cover'}
              disabled={!maskContent}
              onChange={(e) => handleTransformChange('fitMode', e.target.value)}
              className="w-full px-2 py-1 border border-gray-200 rounded"
            >
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
              <option value="fill">Fill</option>
            </select>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg p-4 space-y-4">
        <p className="text-sm font-medium text-gray-700">Mask Settings</p>
        <div className="space-y-3">
          <div>
            <label className="flex items-center justify-between text-xs text-gray-500">
              <span>Feather</span>
              <span>{maskSettings.feather || 0}px</span>
            </label>
            <input
              type="range"
              min="0"
              max="50"
              value={maskSettings.feather || 0}
              onChange={(e) => handleMaskSettingChange('feather', parseInt(e.target.value, 10) || 0)}
              className="w-full"
            />
          </div>

          <div>
            <label className="flex items-center justify-between text-xs text-gray-500">
              <span>Blur</span>
              <span>{maskSettings.blur || 0}px</span>
            </label>
            <input
              type="range"
              min="0"
              max="50"
              value={maskSettings.blur || 0}
              onChange={(e) => handleMaskSettingChange('blur', parseInt(e.target.value, 10) || 0)}
              className="w-full"
            />
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Mask Opacity</span>
            <span>{Math.round((maskSettings.opacity ?? 1) * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={maskSettings.opacity ?? 1}
            onChange={(e) => handleMaskSettingChange('opacity', parseFloat(e.target.value))}
            className="w-full"
          />

          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={maskSettings.invert || false}
              onChange={(e) => handleMaskSettingChange('invert', e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>Invert mask (swap white/transparent)</span>
          </label>
        </div>
      </div>
    </div>
  )
}

export default MaskElementProperties
